<template>
    <div class="modal fade" id="stock_location_modal" ref="modalEl" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered mw-520px">
            <div class="modal-content">
                <div class="modal-header">
                    <h2 class="fw-bolder">{{ $t('pages.stock.location.addTitle') }}</h2>
                    <div data-bs-dismiss="modal" class="btn btn-icon btn-sm btn-active-icon-primary">
                        <span class="svg-icon svg-icon-1"><inline-svg src="/media/icons/duotune/arrows/arr061.svg"/></span>
                    </div>
                </div>

                <el-form @submit.prevent="onSubmit" :model="form" ref="formRef">
                    <div class="modal-body py-8 px-lg-10">
                        <div class="sld-field">
                            <label class="sld-label sld-label--required">{{ $t('pages.stock.location.warehouse') }}</label>
                            <el-select v-model="form.warehouse_id" class="w-100"
                                :placeholder="$t('pages.stock.location.warehousePlaceholder')"
                                filterable>
                                <el-option v-for="w in availableWarehouses" :key="w.id"
                                    :value="w.id"
                                    :label="warehouseLabel(w)"/>
                            </el-select>
                            <span v-if="availableWarehouses.length === 0" class="sld-hint">
                                {{ $t('pages.stock.location.allWarehousesUsed') }}
                            </span>
                        </div>
                    </div>

                    <div class="modal-footer flex-center">
                        <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal" :disabled="loading">
                            {{ $t('common.cancel') }}
                        </button>
                        <button type="submit" class="btn btn-primary"
                            :disabled="!canSubmit || loading"
                            :data-kt-indicator="loading ? 'on' : null">
                            <span v-if="!loading" class="indicator-label">{{ $t('common.save') }}</span>
                            <span v-if="loading" class="indicator-progress">
                                {{ $t('messages.wait') }}
                                <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        </button>
                    </div>
                </el-form>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "StockLocationDialog",
    props: {
        stockId: { type: [Number, String], required: true },
        existingLocations: { type: Array, default: () => [] },
    },
    emits: ['created'],
    data() {
        return {
            form: { warehouse_id: null },
            loading: false,
            warehouses: [],
        };
    },
    computed: {
        existingWarehouseIds() {
            return new Set((this.existingLocations || []).map(l => l.warehouse_id));
        },
        availableWarehouses() {
            return this.warehouses.filter(w => !this.existingWarehouseIds.has(w.id));
        },
        canSubmit() {
            return !!this.form.warehouse_id;
        },
    },
    methods: {
        warehouseLabel(w) {
            return w.name + (w.is_default ? ' · ' + this.$t('pages.cash_register.defaultBadge') : '');
        },
        async open() {
            this.form = { warehouse_id: null };
            await this.fetchWarehouses();
            this.showModal(this.$refs.modalEl);
        },
        async fetchWarehouses() {
            await this.$store.dispatch('stock/warehouse/get', { page: { pageSize: 9999 } });
            this.warehouses = this.$store.state.stock.warehouse.table?.data || [];
        },
        async onSubmit() {
            if (!this.canSubmit) return;
            this.loading = true;
            try {
                const res = await this.$store.dispatch('stock/location/add', {
                    stockId: this.stockId,
                    formData: { warehouse_id: this.form.warehouse_id },
                });
                if (res?.status) {
                    this.$message.success(this.$t('messages.saveOk'));
                    this.hideModal(this.$refs.modalEl);
                    this.$emit('created', res.data);
                }
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.sld-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 14px;
}

.sld-label {
    font-size: 12px;
    font-weight: 600;
    color: #1c1c1e;
}

.sld-label--required::after {
    content: ' *';
    color: #ef4444;
}

.sld-hint {
    font-size: 11.5px;
    color: #8e8e93;
}

html.dark .sld-label,
html.midnight .sld-label { color: #EAE6E1; }

html.dark .sld-hint,
html.midnight .sld-hint { color: #9A9CAE; }

html.cream .sld-label { color: #3D3832; }
html.cream .sld-hint { color: #847C72; }

/* Modal cancel button — Metronic btn-light tum temalarda override */
.btn.btn-light,
.btn.btn-light:hover,
.btn.btn-light:focus,
.btn.btn-light:active,
.btn.btn-light:focus-visible {
    border-color: rgba(0, 0, 0, 0.08) !important;
    color: #1c1c1e !important;
    box-shadow: none !important;
}

.btn.btn-light,
.btn.btn-light:focus { background-color: rgba(0, 0, 0, 0.05) !important; }
.btn.btn-light:hover,
.btn.btn-light:active { background-color: rgba(0, 0, 0, 0.09) !important; }

html.dark .btn.btn-light,
html.dark .btn.btn-light:hover,
html.dark .btn.btn-light:focus,
html.dark .btn.btn-light:active,
html.midnight .btn.btn-light,
html.midnight .btn.btn-light:hover,
html.midnight .btn.btn-light:focus,
html.midnight .btn.btn-light:active {
    border-color: rgba(255, 255, 255, 0.1) !important;
    color: #EAE6E1 !important;
}

html.dark .btn.btn-light,
html.dark .btn.btn-light:focus,
html.midnight .btn.btn-light,
html.midnight .btn.btn-light:focus { background-color: rgba(255, 255, 255, 0.07) !important; }
html.dark .btn.btn-light:hover,
html.dark .btn.btn-light:active,
html.midnight .btn.btn-light:hover,
html.midnight .btn.btn-light:active { background-color: rgba(255, 255, 255, 0.13) !important; }

html.cream .btn.btn-light,
html.cream .btn.btn-light:hover,
html.cream .btn.btn-light:focus,
html.cream .btn.btn-light:active {
    border-color: rgba(61, 56, 50, 0.1) !important;
    color: #3D3832 !important;
}

html.cream .btn.btn-light,
html.cream .btn.btn-light:focus { background-color: rgba(61, 56, 50, 0.06) !important; }
html.cream .btn.btn-light:hover,
html.cream .btn.btn-light:active { background-color: rgba(61, 56, 50, 0.1) !important; }

</style>
